// @ts-check
// =============================================================================
// js/render/card-images.js — shared "give me this card as an <img>" helper.
//
// Used by every DOM screen that shows finished cards (gallery, deck builder,
// rewards). Wraps the preserved generator's renderCardToImage with the
// IndexedDB cache so grids are fast on revisit, plus the shared fullscreen
// zoom popup.
// =============================================================================

import { initCardRenderer, gen, resolveCardArt } from './card-tile.js';
import { loadImage, saveImage } from './image-cache.js';
import { escapeHtml } from '../ui/modal.js';

/**
 * The "still rendering" stand-in a card grid shows: the dedicated LOADING
 * frame (/gui/loading/Frame_<Type>_Loading.png) matching the card's cost
 * color (green crop / red meat / yellow wait / purple free — same branch
 * order as the generator picks real frames). Its 1917×2688 canvas has the
 * same aspect as finished renders, so nothing shifts when the image lands.
 * @param {any} [card]  Falls back to the free (purple) frame without one.
 * @returns {string} HTML
 */
export function cardLoadingHtml(card) {
	let frame = 'Free';
	if (card?.cropCost) frame = 'Crop';
	else if (card?.meatCost) frame = 'Meat';
	else if (card?.waitCost || card?.trueForm) frame = 'Wait';
	return `<img class="gallery-card-img card-loading" src="/gui/loading/Frame_${frame}_Loading.png" alt="Loading card">`;
}

/**
 * Render a card to an image URL, with IndexedDB caching.
 * Cache key = card + art (the whole cache is wiped when VERSION changes, so
 * the version isn't part of the key).
 * @param {any} card
 * @returns {Promise<string|null>}  blob:/data: URL, or null on failure.
 */
export async function renderCardCached(card) {
	await initCardRenderer();
	// Resolve the displayed art FIRST — the cache key depends on it (deck
	// entries and engine clones arrive with selectedArt unset/0).
	resolveCardArt(card);
	const key = `card_${card.id}_${card.selectedArt || 'none'}`;
	const cached = await loadImage(key);
	if (cached) return cached;
	try {
		const img = await gen.renderCardToImage(card);
		// Persist for next visit (data URL -> Blob). Failure is non-fatal.
		try {
			const blob = await (await fetch(img.src)).blob();
			await saveImage(key, blob);
		} catch {
			/* cache write failed — still return the image */
		}
		return img.src;
	} catch (error) {
		console.error('card render failed for', card?.name, error);
		return null;
	}
}

/** Hide the shared fullscreen zoom overlay, if it's open. */
export function hideImageZoom() {
	const popup = document.getElementById('shared-zoom');
	if (!popup) return;
	popup.classList.remove('show');
	popup.style.display = 'none';
}

/**
 * Show an image in the shared fullscreen zoom overlay.
 *
 * Two dismissal modes, picked by HOW the zoom was opened:
 *   - HOLD-opened (deck builder's wireCardPress): the pointer is still down
 *     on the CARD, and the release closes the zoom (release -> hideImageZoom).
 *     The overlay must stay pointer-transparent (.popup's pointer-events:
 *     none) — if it hit-tested, the overlay appearing under a held mouse
 *     cursor would fire pointerleave on the card and end the peek instantly.
 *   - TAP-opened (the gallery): the pointer is already up when the zoom
 *     appears, so the only way out is tapping the overlay itself. Pass
 *     `tapToClose: true` — it adds the .tap-close class, which restores
 *     pointer-events so the pointerup/click handlers below can actually fire.
 *     Without it the overlay is untouchable and every click lands on the
 *     card grid BENEATH the zoom, just switching which card is zoomed.
 * @param {string} src
 * @param {string} [name]
 * @param {{tapToClose?: boolean}} [options]
 * @returns {void}
 */
export function showImageZoom(src, name = '', { tapToClose = false } = {}) {
	let popup = document.getElementById('shared-zoom');
	if (!popup) {
		popup = document.createElement('div');
		popup.id = 'shared-zoom';
		popup.className = 'popup';
		popup.addEventListener('pointerup', hideImageZoom);
		popup.addEventListener('click', hideImageZoom);
		document.body.appendChild(popup);
	}
	// Set per call — the overlay is shared, so a gallery zoom must not leave
	// the class behind for a later hold-zoom (and vice versa).
	popup.classList.toggle('tap-close', tapToClose);
	popup.innerHTML = `<img src="${src}" alt="${escapeHtml(name)}">`;
	popup.style.display = 'flex';
	popup.classList.add('show');
}
